import { useLayoutEffect, useRef, type InputHTMLAttributes } from 'react'
import { groupAmount, sanitizeAmountTyping } from '@globalx/core'

/**
 * Amount field that groups digits live while typing and keeps the caret
 * in place. The stored value is always the raw ASCII amount.
 */
export function AmountInput({
  value,
  onValueChange,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string
  onValueChange: (raw: string) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const caret = useRef<number | null>(null) // significant chars left of the caret
  const display = groupAmount(value)

  useLayoutEffect(() => {
    const el = ref.current
    const n = caret.current
    if (!el || n === null || document.activeElement !== el) return
    caret.current = null
    let pos = 0
    for (let seen = 0; pos < display.length && seen < n; pos++) if (display[pos] !== ',') seen++
    el.setSelectionRange(pos, pos)
  }, [display])

  return (
    <input
      ref={ref}
      inputMode="decimal"
      autoComplete="off"
      dir="ltr"
      value={display}
      onChange={(e) => {
        const el = e.target
        caret.current = sanitizeAmountTyping(el.value.slice(0, el.selectionStart ?? el.value.length)).length
        onValueChange(sanitizeAmountTyping(el.value))
      }}
      {...props}
    />
  )
}
